import { quoteLiteral } from './names'
/** Token positions retain verbatim SQL for rebuilds. Comments/strings never become identifiers. */
export interface Token {
  value: string
  raw: string
  start: number
  end: number
  depth: number
  type: 'word' | 'identifier' | 'string' | 'symbol' | 'number' | 'parameter'
}

export function tokenize(sql: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  let depth = 0
  while (i < sql.length) {
    const char = sql[i]
    if (/\s/u.test(char)) { i++; continue }
    if (sql.startsWith('--', i)) {
      const end = sql.indexOf('\n', i + 2)
      i = end < 0 ? sql.length : end + 1
      continue
    }
    if (sql.startsWith('/*', i)) {
      const end = sql.indexOf('*/', i + 2)
      if (end < 0) throw new Error('Unterminated SQL comment')
      i = end + 2
      continue
    }
    const start = i
    let value = ''
    let type: Token['type'] = 'symbol'
    if ('"`[\''.includes(char)) {
      const close = char === '[' ? ']' : char
      type = char === "'" ? 'string' : 'identifier'
      i++
      let closed = false
      while (i < sql.length) {
        if (sql[i] === close) {
          if (char !== '[' && sql[i + 1] === close) { value += close; i += 2; continue }
          i++
          closed = true
          break
        }
        value += sql[i++]
      }
      if (!closed) throw new Error('Unterminated SQL quote')
    } else if ('?:@$'.includes(char)) {
      i++
      while (i < sql.length && /[\p{L}\p{N}_]/u.test(sql[i])) i++
      value = sql.slice(start, i)
      type = 'parameter'
    } else if (/[^\s(),;.=+*/%<>!|&~?:'"`[\]-]/u.test(char)) {
      i++
      while (i < sql.length && /[^\s(),;.=+*/%<>!|&~?:'"`[\]-]/u.test(sql[i])) i++
      value = sql.slice(start, i)
      type = /^\d/u.test(value) ? 'number' : 'word'
    } else {
      value = char
      i++
    }
    if (type === 'symbol' && value === ')') depth--
    if (depth < 0) throw new Error('Unbalanced SQL parentheses')
    tokens.push({ value, raw: sql.slice(start, i), start, end: i, depth, type })
    if (type === 'symbol' && value === '(') depth++
  }
  if (depth !== 0) throw new Error('Unbalanced SQL parentheses')
  return tokens
}

export function keyword(token: Token | undefined, word: string): boolean {
  return token?.type === 'word' && token.value.toUpperCase() === word
}

export function identifier(token: Token | undefined): boolean {
  return token?.type === 'word' || token?.type === 'identifier'
}

export function mentions(sql: string, name: string): boolean {
  const normalized = name.normalize('NFC').toUpperCase().toLowerCase()
  return tokenize(sql).some(token => identifier(token) &&
    token.value.normalize('NFC').toUpperCase().toLowerCase() === normalized)
}

export function splitStatements(sql: string): string[] {
  const parts: string[] = []
  let start = 0
  for (const token of tokenize(sql)) {
    if (token.type === 'symbol' && token.value === ';' && token.depth === 0) {
      const part = sql.slice(start, token.start).trim()
      if (tokenize(part).length > 0) parts.push(part)
      start = token.end
    }
  }
  const last = sql.slice(start).trim()
  if (tokenize(last).length > 0) parts.push(last)
  return parts
}

/** Stored CREATE TABLE body and suffix (e.g. STRICT) without reconstructing declarations. */
export function tableDefinition(sql: string): { parts: string[]; suffix: string } {
  const tokens = tokenize(sql)
  const open = tokens.find(token => token.type === 'symbol' && token.value === '(' && token.depth === 0)
  const close = tokens.find(token => token.type === 'symbol' && token.value === ')' && token.depth === 0)
  if (!open || !close) throw new Error('Expected a CREATE TABLE body')
  const parts: string[] = []
  let start = open.end
  for (const token of tokens) {
    if (token.start > open.start && token.start < close.start && token.type === 'symbol' && token.value === ',' && token.depth === 1) {
      parts.push(sql.slice(start, token.start).trim())
      start = token.end
    }
  }
  parts.push(sql.slice(start, close.start).trim())
  return { parts, suffix: sql.slice(close.end).trim().replace(/;$/u, '') }
}

/** PRAGMA table_xinfo omits array suffixes; read declared types from SQL. */
export function tableColumns(sql: string): { name: string; type: string; sql: string }[] {
  return tableDefinition(sql).parts.filter(part => !tableConstraint(part)).map(part => {
    const tokens = tokenize(part)
    const constraint = tokens.find(token => token.depth === 0 && ['PRIMARY', 'NOT', 'NULL', 'UNIQUE', 'CHECK', 'DEFAULT', 'COLLATE', 'REFERENCES', 'GENERATED', 'AS', 'CONSTRAINT'].some(word => keyword(token, word)))
    return { name: tokens[0]?.value ?? '', type: part.slice(tokens[1]?.start ?? part.length, constraint?.start ?? part.length).trim(), sql: part }
  })
}

export function tableConstraint(part: string): boolean {
  return ['CONSTRAINT', 'PRIMARY', 'UNIQUE', 'CHECK', 'FOREIGN'].some(word => keyword(tokenize(part)[0], word))
}

/** Stock ALTER may serialize an inline REFERENCES as a single-column table FK. */
export function ownForeignKey(part: string, column: string): boolean {
  const tokens = tokenize(part)
  const at = keyword(tokens[0], 'CONSTRAINT') ? 2 : 0
  return keyword(tokens[at], 'FOREIGN') && keyword(tokens[at + 1], 'KEY') &&
    tokens[at + 2]?.raw === '(' && identifier(tokens[at + 3]) &&
    tokens[at + 3].value.normalize('NFC').toUpperCase().toLowerCase() === column.normalize('NFC').toUpperCase().toLowerCase() &&
    tokens[at + 4]?.raw === ')' && keyword(tokens[at + 5], 'REFERENCES')
}


/** Bind replay SQL, including BLOBs, without relying on JSON's value coercions. */
export function bindParams(sql: string, params: readonly unknown[] = []): string {
  let next = 0
  let end = 0
  let result = ''
  let used = 0
  for (const token of tokenize(sql)) {
    if (token.type !== 'parameter') continue
    if (!/^\?(?:[1-9]\d*)?$/u.test(token.raw)) throw new Error('Only positional SQL bindings are supported')
    const index = token.raw.length === 1 ? next : Number(token.raw.slice(1)) - 1
    if (index >= params.length) throw new Error('Missing SQL binding')
    next = Math.max(next, index + 1)
    used = Math.max(used, index + 1)
    const value = params[index]
    let literal: string
    if (value === null) literal = 'NULL'
    else if (typeof value === 'string') literal = quoteLiteral(value)
    else if (typeof value === 'number' && Number.isFinite(value)) literal = String(value)
    else if (typeof value === 'bigint') literal = String(value)
    else if (typeof value === 'boolean') literal = value ? '1' : '0'
    else if (value instanceof Date) literal = quoteLiteral(value.toISOString())
    else if (value instanceof Uint8Array) literal = `X'${[...value].map(byte => byte.toString(16).padStart(2, '0')).join('')}'`
    else throw new Error('Unsupported SQL binding')
    result += sql.slice(end, token.start) + literal
    end = token.end
  }
  if (used !== params.length) throw new Error('Unexpected SQL bindings')
  return result + sql.slice(end)
}

/** Only the index expression/predicate, excluding the index and table identifiers. */
export function indexExpression(sql: string): string {
  const tokens = tokenize(sql)
  const on = tokens.findIndex(token => keyword(token, 'ON'))
  if (on < 0) return ''
  const end = tokens[on + 2]?.raw === '.' ? tokens[on + 3]?.end : tokens[on + 1]?.end
  return sql.slice(end ?? sql.length)
}

/** SET targets only; identifiers inside right-hand expressions are reads. */
export function assignmentColumns(sql: string): string[] {
  const tokens = tokenize(sql)
  const columns: string[] = []
  for (const [at, token] of tokens.entries()) {
    if (!keyword(token, 'SET') || token.depth !== 0) continue
    let left = true
    for (const candidate of tokens.slice(at + 1)) {
      if (candidate.depth === 0 && ['WHERE', 'RETURNING', 'ORDER', 'LIMIT'].some(word => keyword(candidate, word))) break
      if (candidate.raw === '=' && candidate.depth === 0) left = false
      if (candidate.raw === ',' && candidate.depth === 0) left = true
      if (left && (identifier(candidate) || candidate.type === 'string')) columns.push(candidate.value)
    }
  }
  return columns
}
